import { useMemo } from "react";
import { Merchant } from "@/dto/merchant";
import {
  FEATURE_FLAG_PAZE_ON_COLO2,
  FEATURE_FLAG_HORIZONTAL_SCROLL_ON_COLO2,
  FEATURE_FLAG_QUANTUM_METRIC,
  FEATURE_FLAG_LOCALIZATION_ON_COLO2,
  DEPOSIT_TAKING_FOR_SERVICES_ENABLED,
  FEATURE_FLAG_EARLY_ERROR_PREVENTION,
  FEATURE_FLAG_AU_SURCHARGE_WARNING,
} from "@/utils/constants";

export type FeatureFlagName =
  | typeof FEATURE_FLAG_PAZE_ON_COLO2
  | typeof FEATURE_FLAG_HORIZONTAL_SCROLL_ON_COLO2
  | typeof FEATURE_FLAG_QUANTUM_METRIC
  | typeof FEATURE_FLAG_LOCALIZATION_ON_COLO2
  | typeof DEPOSIT_TAKING_FOR_SERVICES_ENABLED
  // Bot-neutralization / early-error-prevention gate. Read from the merchant's
  // launchDarklyFeatures (see isEarlyErrorPreventionEnabled in
  // botNeutralization.ts). At the proxy layer no merchant is resolved yet, so that
  // layer instead honors the server-level EARLY_ERROR_PREVENTION_ENABLED env
  // mirror; this hook is for use wherever a merchant is available.
  | typeof FEATURE_FLAG_EARLY_ERROR_PREVENTION
  | typeof FEATURE_FLAG_AU_SURCHARGE_WARNING;

/**
 * Checks if a specific LaunchDarkly feature flag is present and enabled for a merchant.
 * * @param merchant - The merchant object containing launchDarklyFeatures
 * @param flagName - The specific flag name to check
 * @returns boolean - True if the flag exists and is enabled
 */
export const useMerchantFeatureFlag = (
  merchant: Merchant | undefined | null,
  flagName: FeatureFlagName
): boolean => {
  return useMemo(() => {
    if (!merchant?.launchDarklyFeatures) {
      return false;
    }

    const hasFlag = merchant.launchDarklyFeatures.some(
      (feature) => feature.name === flagName && feature.enabled
    );

    return hasFlag;
  }, [merchant?.launchDarklyFeatures, flagName]);
};
